import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Snackbar } from "@mui/material";
import { TaskNotification, TaskSummary, taskService } from "../../services/taskService";
import { useStaffNames } from "./useStaffNames";
import { describeNotification } from "./taskUtils";

const SEEN_KEY = "tasks.alerts.seen";
const POP_KINDS = new Set(["assigned", "mention"]);

const readSeen = (): Set<string> => {
    try {
        return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
    } catch {
        return new Set();
    }
};
const writeSeen = (seen: Set<string>) => {
    try {
        sessionStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen).slice(-100)));
    } catch {
        // per-tab convenience only
    }
};

interface TaskAlertsProps {
    summary: TaskSummary | null;
    onRead: () => void;
}

/**
 * "Rahim assigned you: Call customer…" — a pop-up when a new assignment
 * or mention arrives (the summary is polled every 60s and on tab focus).
 * Alerts already waiting when the admin opens aren't popped; the bell
 * lists them.
 */
export const TaskAlerts: React.FC<TaskAlertsProps> = ({ summary, onRead }) => {
    const navigate = useNavigate();
    const seen = useRef<Set<string> | null>(null);
    const [current, setCurrent] = useState<TaskNotification | null>(null);
    const names = useStaffNames([current?.actor_user_id]);

    useEffect(() => {
        if (!summary) return;
        const unread = summary.latest_unread;
        if (seen.current === null) {
            // First look this tab: remember what's already there.
            seen.current = readSeen();
            if (seen.current.size === 0) {
                unread.forEach((n) => seen.current!.add(n.notification_id));
                writeSeen(seen.current);
                return;
            }
        }
        const fresh = unread.filter((n) => !seen.current!.has(n.notification_id) && POP_KINDS.has(n.kind));
        unread.forEach((n) => seen.current!.add(n.notification_id));
        writeSeen(seen.current);
        if (fresh.length) setCurrent(fresh[0]);
    }, [summary]);

    if (!current) return null;
    const actor = current.actor_user_id ? names.get(current.actor_user_id) || "Someone" : "Someone";
    const open = async () => {
        setCurrent(null);
        navigate(`/tasks?task=${current.task_id}`);
        try {
            await taskService.markNotificationsRead([current.notification_id]);
        } finally {
            onRead();
        }
    };

    return (
        <Snackbar
            open
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            autoHideDuration={12000}
            onClose={(_, reason) => reason !== "clickaway" && setCurrent(null)}
            sx={{ mt: 7 }}
        >
            <Alert
                severity="info"
                variant="filled"
                icon={false}
                onClose={() => setCurrent(null)}
                action={
                    <Button color="inherit" size="small" onClick={open} sx={{ fontWeight: 700 }}>
                        Open
                    </Button>
                }
                sx={{ bgcolor: "#18383E", alignItems: "center", maxWidth: 420 }}
            >
                <strong>{describeNotification(current.kind, actor)}</strong>
                {current.kind === "assigned" ? ": " : " "}
                {current.task_title}
            </Alert>
        </Snackbar>
    );
};
